import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, Plus, Settings } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { toast } from 'sonner';

export default function StandardsPage() {
  const { organization } = useAuth();
  const { toast: toastHook } = useToast();
  const queryClient = useQueryClient();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    key: '',
    name: '',
  });

  const { data: standards, isLoading, refetch } = useQuery({
    queryKey: ['standards', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      
      const { data, error } = await supabase
        .from('standards')
        .select('*')
        .eq('org_id', organization.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!organization?.id,
  });

  const toggleStandard = async (id: string, enabled: boolean) => {
    try {
      const { error } = await supabase
        .from('standards')
        .update({ enabled })
        .eq('id', id);

      if (error) throw error;

      toastHook({
        title: enabled ? 'Standard enabled' : 'Standard disabled',
        description: `The standard has been ${enabled ? 'enabled' : 'disabled'} successfully.`,
      });

      refetch();
    } catch (error) {
      toastHook({
        title: 'Error',
        description: 'Failed to update standard status.',
        variant: 'danger',
      });
    }
  };

  const createStandard = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (!organization?.id) throw new Error('No organization');
      
      const { error } = await supabase.from('standards').insert([{
        key: data.key,
        name: data.name,
        enabled: true,
        org_id: organization.id,
      }]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Standard created successfully');
      queryClient.invalidateQueries({ queryKey: ['standards'] });
      setAddDialogOpen(false);
      setFormData({ key: '', name: '' });
    },
    onError: () => {
      toast.error('Failed to create standard');
    },
  });

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Standards</h1>
            <p className="text-muted-foreground">Manage compliance standards for your organization</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-3 bg-muted rounded w-full mb-2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Standards</h1>
          <p className="text-muted-foreground">Manage compliance standards for your organization</p>
        </div>
        <Button className="gap-2" onClick={() => setAddDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Standard
        </Button>
      </div>

      {standards && standards.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {standards.map((standard) => (
            <Card key={standard.id} className="relative">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    {standard.key}
                  </CardTitle>
                  <Switch
                    checked={standard.enabled || false}
                    onCheckedChange={(enabled) => toggleStandard(standard.id, enabled)}
                  />
                </div>
                <CardDescription>{standard.name}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <Badge variant={standard.enabled ? 'default' : 'secondary'}>
                    {standard.enabled ? 'Active' : 'Inactive'}
                  </Badge>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <Settings className="h-4 w-4" />
                    Configure
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Shield className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No standards found</h3>
            <p className="text-muted-foreground text-center mb-4">
              Get started by adding compliance standards to your organization.
            </p>
            <Button className="gap-2" onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              Add Your First Standard
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Standard</DialogTitle>
            <DialogDescription>
              Add a compliance standard to track for your organization
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="key">Standard Key *</Label>
              <Input
                id="key"
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                placeholder="e.g., ISO27001, NIST, SOC2"
              />
            </div>
            
            <div>
              <Label htmlFor="name">Standard Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., ISO 27001:2013"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createStandard.mutate(formData)}
              disabled={!formData.key || !formData.name || createStandard.isPending}
            >
              {createStandard.isPending ? 'Creating...' : 'Create Standard'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}